/**
 * Stage 4 — Export.
 *
 * Loads wiseentry.csv template from app-config, builds rows from extracted data
 * (using effective edits), writes the CSV to the work folder.
 *
 * Warns if any rows are uncoded (GL/property blank) but allows export anyway.
 */

import { getState, setState } from '../state.js';
import { el, clear, toast } from './dom.js';
import { ensureWritable } from '../folders.js';
import {
  loadTemplate,
  buildRowsForFile,
  serializeCsv,
  writeCsvToFolder,
  buildOutputFilename,
  DEFAULT_TEMPLATE_FILENAME
} from '../lib/csv.js';
import { fileIsUncoded } from '../lib/rules.js';

let mounted = null;

export function renderExportStage(mount) {
  clear(mount);
  mounted = mount;

  const state = getState();
  const wrap = el('div');

  wrap.appendChild(el('div', { class: 'path-bar' }, [
    el('div', {}, [
      el('span', { class: 'label' }, ['Export']),
      el('span', { class: 'path' }, [state.workName || ''])
    ]),
    el('div', { class: 'actions' }, [
      el('button', { class: 'ghost-btn', onClick: () => setState({ stage: 'review' }) }, ['← Back to review'])
    ])
  ]));

  const ready  = state.files.filter(f => f.status === 'done');
  const uncoded = ready.filter(f => fileIsUncoded(f));

  if (ready.length === 0) {
    wrap.appendChild(el('div', { class: 'panel panel-empty' }, [
      el('h2', {}, ['Nothing to export']),
      el('p', {}, ['No invoices have been extracted in this batch yet.'])
    ]));
    mount.appendChild(wrap);
    return;
  }

  // Load and display template columns
  const panel = el('div', { class: 'panel' });
  panel.appendChild(el('div', { class: 'field-label' }, [`CSV template (${DEFAULT_TEMPLATE_FILENAME})`]));
  panel.appendChild(el('div', {
    style: { fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '14px' }
  }, ['Defined in your app-config folder. Edit ' + DEFAULT_TEMPLATE_FILENAME + ' to add columns or change order.']));
  const templateInfo = el('div', { id: 'template-info', style: { fontSize: '13px' } }, ['Loading template…']);
  panel.appendChild(templateInfo);
  wrap.appendChild(panel);

  // Filename
  const stamp = buildOutputFilename(state.settings.csvFilenamePrefix);
  const filenameInput = el('input', {
    type: 'text',
    class: 'input-mono',
    style: { width: '100%', maxWidth: '380px' },
    value: stamp
  });
  const filenamePanel = el('div', { class: 'panel' }, [
    el('div', { class: 'field-label' }, ['Output filename']),
    el('div', {
      style: { fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '10px' }
    }, ['Will be written to your work folder.']),
    filenameInput
  ]);
  wrap.appendChild(filenamePanel);

  // Warnings
  const warnings = el('div');
  if (uncoded.length > 0) {
    warnings.appendChild(el('div', {
      class: 'panel',
      style: {
        background: 'var(--warn-bg)',
        borderColor: 'var(--warn)',
        color: 'var(--warn-fg)'
      }
    }, [
      el('div', { style: { fontWeight: '600', marginBottom: '4px' } }, [
        `⚠ ${uncoded.length} of ${ready.length} invoice${ready.length === 1 ? '' : 's'} ${uncoded.length === 1 ? 'is' : 'are'} uncoded`
      ]),
      el('div', { style: { fontSize: '13px' } }, [
        'GL code and property code will be blank for those rows. You can still export, or go back and code them first.'
      ])
    ]));
  }
  wrap.appendChild(warnings);

  // Action
  wrap.appendChild(el('div', { class: 'batch-summary' }, [
    el('div', { class: 'summary-counts' }, [
      el('div', { class: 'count' }, [
        el('span', { class: 'num' }, [String(ready.length)]),
        el('span', { class: 'lbl' }, ['rows'])
      ]),
      uncoded.length > 0 && el('div', { class: 'count' }, [
        el('span', { class: 'num', style: { color: 'var(--warn)' } }, [String(uncoded.length)]),
        el('span', { class: 'lbl' }, ['uncoded'])
      ])
    ]),
    el('div', { class: 'summary-actions' }, [
      el('button', {
        class: 'primary-btn',
        onClick: async () => {
          await runExport(filenameInput.value.trim() || stamp);
        }
      }, [
        uncoded.length > 0 ? `Export ${ready.length} rows anyway` : `Export ${ready.length} rows`
      ])
    ])
  ]));

  mount.appendChild(wrap);

  // Async-load template info and replace placeholder
  loadTemplateAsync(templateInfo);
}

async function loadTemplateAsync(targetEl) {
  const state = getState();
  if (!state.appConfigHandle) {
    targetEl.textContent = 'App-config folder not set.';
    return;
  }
  if (!(await ensureWritable(state.appConfigHandle))) {
    targetEl.textContent = 'Permission denied to app-config folder.';
    return;
  }

  try {
    const template = await loadTemplate(state.appConfigHandle, DEFAULT_TEMPLATE_FILENAME);
    if (template.justCreated) {
      toast('Created wiseentry.csv with default template in your app-config folder.', 'success', 5000);
    }
    clear(targetEl);
    targetEl.appendChild(el('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '6px' } }, [
      ...template.columns.map(c => el('span', {
        class: c.field ? 'coding-pill' : 'coding-pill suggest',
        style: { fontSize: '12px' },
        title: c.field ? `Mapped → ${c.field}` : 'Unknown column — will emit blank for every row'
      }, [c.rawHeader, c.field ? '' : ' ?']))
    ]));
    // Cache for export
    targetEl._template = template;
  } catch (err) {
    targetEl.textContent = `Could not load template: ${err.message}`;
  }
}

async function runExport(filename) {
  const state = getState();
  if (!state.workHandle) { toast('Work folder not set.', 'error'); return; }

  if (!(await ensureWritable(state.workHandle))) {
    toast('Need write permission for the work folder.', 'error');
    return;
  }

  let template;
  try {
    template = await loadTemplate(state.appConfigHandle, DEFAULT_TEMPLATE_FILENAME);
  } catch (err) {
    toast(`Could not load template: ${err.message}`, 'error', 5000);
    return;
  }

  // Build effective rows: merge edits over extracted
  const effectiveFiles = state.files
    .filter(f => f.status === 'done')
    .map(f => ({
      ...f,
      extracted: { ...f.extracted, ...(f.edits || {}) }
    }));

  const rows = [];
  for (const f of effectiveFiles) {
    rows.push(...buildRowsForFile(template.columns, f, state.settings));
  }
  const csv  = serializeCsv(template.columns, rows, { bom: state.settings.csvBom });

  if (!filename.toLowerCase().endsWith('.csv')) filename += '.csv';

  try {
    await writeCsvToFolder(state.workHandle, filename, csv);
    toast(`Exported ${rows.length} distribution line${rows.length === 1 ? '' : 's'} to ${filename}.`, 'success', 5000);
    showExportSuccess(filename, rows.length);
  } catch (err) {
    toast(`Could not write CSV: ${err.message}`, 'error', 6000);
  }
}

function showExportSuccess(filename, rowCount) {
  if (!mounted) return;
  const banner = el('div', {
    class: 'panel',
    style: {
      background: 'var(--success-bg)',
      borderColor: 'var(--success)',
      color: 'var(--success-fg)',
      marginTop: '14px'
    }
  }, [
    el('div', { style: { fontWeight: '600', fontSize: '14px', marginBottom: '4px' } }, [
      `✓ Exported ${rowCount} row${rowCount === 1 ? '' : 's'}`
    ]),
    el('div', { style: { fontSize: '13px', fontFamily: 'var(--font-mono)' } }, [
      `Saved as ${filename} in your work folder.`
    ])
  ]);
  mounted.appendChild(banner);
}
